import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ListFulfillmentsInput } from "@nexxt/contracts/logistics";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the shipments of the sales orders (50-api/logistics.md, ADR-152): one factory, the same keys for
// loaders, screens and mutations. What is cached of one shipment besides its detail (its sync, its shipments) stays
// under `of`, so whatever reloads the shipments reloads it too.

const fulfillments = api.logistics.fulfillments;
const byId = fulfillments[":fulfillmentId"];

type ListPage = InferResponseType<typeof fulfillments.$get, 200>;
export type Fulfillment = ListPage["items"][number];
export type FulfillmentDetail = InferResponseType<(typeof byId)["$get"], 200>;
export type FulfillmentLine = FulfillmentDetail["lines"][number];
export type Shipment = InferResponseType<(typeof byId)["shipments"]["$get"], 200>[number];

/**
 * The filters of the list input (`listFulfillmentsInput`), without the paging the queries add: the two queues of the
 * home are set or absent, as the URL holds them.
 */
export type FulfillmentFilters = Pick<ListFulfillmentsInput, "status" | "location" | "syncStatus" | "q"> & {
  toBook?: true | undefined;
  ackOverdue?: true | undefined;
  /** The sets of the queues "To do", as `Todo` writes them (ADR-302). */
  anyOf?: string | undefined;
};

/** The tabs of the page of a shipment, in the order it shows them. */
export const fulfillmentTabs = ["lines", "shipments", "warehouse", "details"] as const;
export type FulfillmentTab = (typeof fulfillmentTabs)[number];

const PAGE_SIZE = 50;

const keys = {
  all: ["fulfillments"] as const,
  /** Prefix of every cached list: a write puts what it gets back in each of them. */
  lists: ["fulfillments", "list"] as const,
  /** Prefix of what is cached of one shipment besides its detail: its sync and its shipments. */
  of: (fulfillmentId: string) => ["fulfillments", "of", fulfillmentId] as const,
};

export const fulfillmentQueries = {
  ...keys,

  list: (filters: FulfillmentFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          fulfillments.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** The shipments of a sales order: one per lot (ADR-195), one at most for an order locked before the lots. */
  ofSalesOrder: (salesOrderId: string) =>
    queryOptions({
      queryKey: [...keys.all, "of-sales-order", salesOrderId],
      queryFn: async ({ signal }) =>
        (await unwrap(fulfillments.$get({ query: { soId: salesOrderId } }, { init: { signal } }))).items,
    }),

  /** With its lines and the units the warehouse shipped of each. */
  detail: (fulfillmentId: string) =>
    queryOptions({
      queryKey: [...keys.all, "detail", fulfillmentId],
      queryFn: ({ signal }) => unwrap(byId.$get({ param: { fulfillmentId } }, { init: { signal } })),
    }),

  /** What the warehouse reported and what was booked by hand, the first first. */
  shipments: (fulfillmentId: string) =>
    queryOptions({
      queryKey: [...keys.of(fulfillmentId), "shipments"],
      queryFn: ({ signal }) => unwrap(byId.shipments.$get({ param: { fulfillmentId } }, { init: { signal } })),
    }),

  /** How it stands towards the warehouse (I-09 §7). */
  sync: (fulfillmentId: string) =>
    queryOptions({
      queryKey: [...keys.of(fulfillmentId), "sync"],
      queryFn: ({ signal }) => unwrap(byId.sync.$get({ param: { fulfillmentId } }, { init: { signal } })),
    }),
};
